import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DpModuleService } from '../dp-module.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-change-email-dp',
  templateUrl: './change-email-dp.component.html',
  styleUrls: ['./change-email-dp.component.scss']
})
export class ChangeEmailDpComponent implements OnInit {
  dpId: number = 0;
  currentEmail: string = '';
  currentEmailFull: string = '';
  newEmail: string = '';
  dpFullData: any = null;
  newEmailTouched: boolean = false;
  proceedAttempted: boolean = false;
  
  // State management
  isLoading: boolean = false;
  isValidating: boolean = false;
  isProcessing: boolean = false;
  step: 'input' | 'confirmation' = 'input'; // Current step
  
  // Error handling
  errorMessage: string = '';
  validationError: string = '';

  constructor(
    public activeModal: NgbActiveModal,
    private dpService: DpModuleService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.fetchCurrentDpDetails();
  }

  /**
   * Step 1: Fetch current DP details from edit endpoint
   */
  fetchCurrentDpDetails(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const payload = { id: this.dpId };

    this.dpService.editDpModule(payload).subscribe(
      (response: any) => {
        if (response && response.data && response.data.length > 0) {
          this.dpFullData = response.data[0];
          this.currentEmailFull = this.dpFullData.email ? this.dpFullData.email.trim() : '';
          // Extract core email (before | if present)
          this.currentEmail = this.dpFullData.email
            ? this.dpFullData.email.split('|')[0].trim()
            : '';
          this.isLoading = false;
        } else {
          this.errorMessage = 'Failed to fetch DP details. Please try again.';
          this.isLoading = false;
        }
      },
      (error: any) => {
        this.errorMessage = `Error fetching DP details: ${error?.message || 'Unknown error'}`;
        this.isLoading = false;
        console.error('Error fetching DP details:', error);
      }
    );
  }

  /**
   * Returns validation error string for the new email field.
   */
  private getNewEmailValidationError(email: string): string {
    if (!email || email.trim() === '') {
      return 'New email is required';
    }

    const trimmedEmail = email.trim();

    if (trimmedEmail.includes('|')) {
      return 'Please enter only email address. Do not include suffix after |';
    }

    const coreEmail = trimmedEmail.toLowerCase().trim();

    // Block exact no-change submissions first.
    if (this.currentEmailFull && trimmedEmail.toLowerCase() === this.currentEmailFull.toLowerCase()) {
      return 'New email must be different from current email';
    }

    const currentCore = this.currentEmail.toLowerCase().trim();

    // New email must be different from current core email.
    if (coreEmail === currentCore) {
      return 'New email must be different from current email';
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+/;
    if (!emailRegex.test(coreEmail)) {
      return 'Please enter a valid email format (e.g., email@domain.com)';
    }

    return '';
  }

  /**
   * Validate new email input and optionally show the error message.
   */
  validateNewEmail(email: string, showError: boolean = true): boolean {
    const validationMsg = this.getNewEmailValidationError(email);
    if (showError) {
      this.validationError = validationMsg;
    }
    return validationMsg === '';
  }

  onNewEmailChange(): void {
    // Keep button state in sync from first keystroke.
    if (this.newEmailTouched || this.proceedAttempted) {
      this.validationError = this.getNewEmailValidationError(this.newEmail);
    } else if (!this.newEmail || this.newEmail.trim() === '') {
      this.validationError = '';
    }
  }

  onNewEmailBlur(): void {
    this.newEmailTouched = true;
    this.validationError = this.getNewEmailValidationError(this.newEmail);
  }

  shouldShowValidationError(): boolean {
    return this.newEmailTouched || this.proceedAttempted;
  }

  isNewEmailValid(): boolean {
    return this.getNewEmailValidationError(this.newEmail) === '';
  }

  /**
   * Proceed to confirmation step
   */
  proceedToConfirmation(): void {
    this.proceedAttempted = true;
    if (this.validateNewEmail(this.newEmail)) {
      this.step = 'confirmation';
    }
  }

  /**
   * Go back to input step
   */
  goBackToInput(): void {
    this.step = 'input';
    this.proceedAttempted = false;
    this.validationError = this.getNewEmailValidationError(this.newEmail);
  }

  /**
   * Step 3: Soft-delete old DP
   */
  private softDeleteOldDp(): Promise<any> {
    return new Promise((resolve, reject) => {
      const payload = {
        id: this.dpId,
        reasonfordelete: 'Change of email address'
      };

      this.dpService.deleteDpModule(payload).subscribe(
        (response: any) => {
          console.log('Old DP soft-deleted successfully:', response);
          resolve(response);
        },
        (error: any) => {
          console.error('Error soft-deleting old DP:', error);
          reject(error);
        }
      );
    });
  }

  /**
   * Step 4: Create new DP with changed email
   * Only include fields with actual values - completely omit empty strings
   */
  private createNewDpWithChangedEmail(): Promise<any> {
    return new Promise((resolve, reject) => {
      // Build minimal payload with ONLY non-empty fields
      const newDpPayload: any = {};

      // Helper function: only add field if it has a non-empty value
      const addField = (key: string, value: any) => {
        if (value !== undefined && value !== null && value !== '') {
          newDpPayload[key] = value;
        }
      };

      // Add required fields
      addField('dpName', this.dpFullData.dpName);
      addField('dpDesignation', this.dpFullData.dpDesignation);
      addField('email', this.newEmail); // NEW EMAIL
      addField('alternativeemail', this.dpFullData.alternativeemail);
      addField('dpPanNo', this.dpFullData.dpPanNo);
      addField('DocumentType', this.dpFullData.DocumentType);
      addField('dpContactNo', this.dpFullData.dpContactNo);
      addField('dpEducationalInstitute', this.dpFullData.dpEducationalInstitute);
      addField('dpAddress', this.dpFullData.dpAddress);
      addField('dpPastEmp', this.dpFullData.dpPastEmp);
      addField('remarks', this.dpFullData.remarks);
      addField('confidentiality_agreement_date', this.dpFullData.confidentiality_agreement_date);
      addField('confidentiality_agreement_attachment', this.dpFullData.confidentiality_agreement_attachment);

      // Optional ID fields - only if they have values
      addField('dpClientId', this.dpFullData.dpClientId);
      addField('dpIndexId', this.dpFullData.dpIndexId);
      addField('categoryofperson', this.dpFullData.categoryofperson);
      addField('otherscategoryofperson', this.dpFullData.otherscategoryofperson);
      addField('role', this.dpFullData.role);
      addField('label', this.dpFullData.label);

      // Fields that should always be set (non-empty)
      newDpPayload.confidentiality_agreement_entered = 'No';
      newDpPayload.accessToSdd = 'Inactive'; // New DP starts inactive
      
      // self_records should be boolean, not string
      if (this.dpFullData.self_records) {
        newDpPayload.self_records = this.dpFullData.self_records === 'Yes' || this.dpFullData.self_records === true ? true : false;
      } else {
        newDpPayload.self_records = false;
      }

      // Copy array/object fields - ALWAYS include them (empty arrays if needed)
      // Backend expects these to be arrays and will foreach over them
      newDpPayload.project = (this.dpFullData.project && Array.isArray(this.dpFullData.project)) 
        ? this.dpFullData.project 
        : [];
      
      newDpPayload.groupLabelName = (this.dpFullData.groupLabelName && Array.isArray(this.dpFullData.groupLabelName)) 
        ? this.dpFullData.groupLabelName 
        : [];
      
      newDpPayload.immediateDpRelative = (this.dpFullData.immediateDpRelative && Array.isArray(this.dpFullData.immediateDpRelative)) 
        ? this.dpFullData.immediateDpRelative 
        : [];

      // Backend expects checkboxgroup as an object, then json_encodes it server-side.
      const defaultCheckboxgroup = {
        equityshares: '',
        preferenceshares: '',
        warrantsshares: '',
        convertibledebenturesshares: '',
        nonconvertibledebenturesshares: '',
        rightsentitlementsshares: '',
        othersshares: ''
      };

      let parsedCheckboxgroup: any = {};
      if (typeof this.dpFullData.checkboxgroup === 'string') {
        try {
          parsedCheckboxgroup = JSON.parse(this.dpFullData.checkboxgroup);
        } catch (e) {
          parsedCheckboxgroup = {};
        }
      } else if (this.dpFullData.checkboxgroup && typeof this.dpFullData.checkboxgroup === 'object') {
        parsedCheckboxgroup = this.dpFullData.checkboxgroup;
      }

      newDpPayload.checkboxgroup = {
        ...defaultCheckboxgroup,
        ...parsedCheckboxgroup
      };

      // Old shares snapshot is also read by backend declaration logic.
      newDpPayload.oldcheckboxgroupArr = {
        ...defaultCheckboxgroup,
        ...newDpPayload.checkboxgroup
      };

      // Ensure any other expected fields have safe defaults
      if (!newDpPayload.user_access) {
        newDpPayload.user_access = [];
      }
      if (!newDpPayload.withWhom) {
        newDpPayload.withWhom = '';
      }

      console.log('Cleaned minimal payload (empty strings omitted):', newDpPayload);

      this.dpService.addDpModule(newDpPayload).subscribe(
        (response: any) => {
          console.log('New DP created successfully:', response);
          resolve(response);
        },
        (error: any) => {
          console.error('Error creating new DP:', error);
          reject(error);
        }
      );
    });
  }

  /**
   * Complete workflow: Delete old, Create new
   */
  async proceedWithEmailChange(): Promise<void> {
    this.proceedAttempted = true;
    if (!this.validateNewEmail(this.newEmail)) {
      return;
    }

    this.isProcessing = true;
    this.errorMessage = '';
    let deleteSucceeded = false;

    try {
      // Step 1: Soft-delete old DP
      await this.softDeleteOldDp();
      deleteSucceeded = true;
      this.toastr.success('Old DP marked as inactive', 'Success');

      // Step 2: Create new DP
      const createResponse = await this.createNewDpWithChangedEmail();
      this.toastr.success('New DP created with new email successfully', 'Success');

      console.log('Email change workflow completed successfully');
      this.isProcessing = false;

      // Close modal and pass success data
      this.activeModal.close({
        success: true,
        oldDpId: this.dpId,
        newEmail: this.newEmail,
        newDpResponse: createResponse
      });

    } catch (error: any) {
      this.isProcessing = false;
      const errorMsg = error?.error?.message || error?.message || 'Unknown error occurred';

      if (deleteSucceeded) {
        this.errorMessage = `Create failed after old DP was marked inactive: ${errorMsg}. Please contact administrator.`;
        this.toastr.error(this.errorMessage, 'Error');
      } else {
        // Delete failed, no rollback needed
        this.errorMessage = `Failed to change email (delete step): ${errorMsg}`;
        this.toastr.error(this.errorMessage, 'Error');
      }
      
      console.error('Email change workflow error:', error);
    }
  }

  /**
   * Cancel workflow
   */
  cancel(): void {
    this.activeModal.dismiss('User cancelled');
  }

  /**
   * Check if Proceed button should be disabled
   */
  shouldDisableProceed(): boolean {
    return !this.isNewEmailValid();
  }
}
